import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../stores/authStore';
import { useCreditStore } from '../stores/creditStore';
import { AuthCore } from '../core/AuthCore';

const LoadingPage: React.FC = () => {
  const [loadingMessage, setLoadingMessage] = useState('Connecting to server...');
  const [dots, setDots] = useState('');
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();

  // Animated dots effect
  useEffect(() => {
    const interval = setInterval(() => {
      setDots(prev => {
        if (prev === '...') return '';
        return prev + '.';
      });
    }, 500);

    return () => clearInterval(interval);
  }, []);

  // Auto-connect to backend
  useEffect(() => {
    const connectToBackend = async () => {
      try {
        if (!user) {
          setLoadingMessage('Authentication required');
          setTimeout(() => navigate('/login'), 2000);
          return;
        }

        setLoadingMessage('Verifying authentication');
        
        // Check if user is authenticated
        if (isAuthenticated) {
          setLoadingMessage('Authentication verified! Redirecting');
          
          // Refresh credit balance after successful authentication
          try {
            // Ensure AuthCore is fully ready before fetching credit balance
            const authState = AuthCore.getInstance().getState();
            if (authState.isAuthenticated && authState.user && !authState.isLoading) {
              await useCreditStore.getState().fetchCreditBalance();
              // [PRODUCTION] Removed success emoji logging;
            }
          } catch (error) {
            console.error('❌ Failed to refresh credit balance:', error);
            // Continue with redirect even if credit refresh fails
          }
          
          setTimeout(() => {
            // Check if there's a saved redirect URL from OAuth flow
            const savedRedirect = localStorage.getItem('auth_redirect');
            if (savedRedirect) {
              localStorage.removeItem('auth_redirect');
              navigate(savedRedirect);
            } else {
              navigate('/my-account');
            }
          }, 1000);
        } else {
          // Authentication failed
          console.error('[LoadingPage] Authentication verification failed');
          throw new Error('Authentication verification failed');
        }
      } catch (error) {
        console.error('Backend connection failed:', error);
        setLoadingMessage('Connection failed. Redirecting to login');
        
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    connectToBackend();
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
        <div className="text-center">
          {/* Loading Spinner */}
          <div className="mb-6">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
          
          {/* Loading Message */}
          <h2 className="text-xl font-semibold text-gray-800 mb-2">
            Wait{dots}
          </h2>
          
          <p className="text-gray-600 mb-4">
            {loadingMessage}{dots}
          </p>
          
          {/* Progress Indicator */}
          <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
            <div className="bg-blue-600 h-2 rounded-full animate-pulse" style={{width: '60%'}}></div>
          </div>
          
          <p className="text-sm text-gray-500">
            Setting up your workspace...
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoadingPage;